import { card, person } from "@universityofmaryland/web-elements-library/composite";
import { createTextWithLink, createTextContainer, createImageOrLinkedImage } from "@universityofmaryland/web-utilities-library/elements";
const CONTACT_CONFIGS = [
  {
    key: "email",
    label: () => "Email",
    url: (value) => `mailto:${value}`
  },
  {
    key: "website",
    label: (value) => value,
    url: (value) => value
  },
  {
    key: "linkedin",
    label: (value) => value,
    url: (value) => value
  },
  {
    key: "twitter",
    label: (value) => value,
    url: (value) => value
  },
  {
    key: "bluesky",
    label: (value) => value,
    url: (value) => value
  },
  {
    key: "substack",
    label: (value) => value,
    url: (value) => value
  }
];
const buildFullName = (entry) => {
  const parts = [
    entry.prefix,
    entry.firstName,
    entry.middleName,
    entry.lastName,
    entry.suffix
  ].filter(Boolean);
  return parts.join(" ");
};
const buildProfileUrl = (entry) => {
  return `https://umdrightnow.umd.edu/expert/${entry.slug}`;
};
const extractPrimaryJobTitle = (entry) => {
  return entry.organizations?.[0]?.jobs?.[0]?.title || null;
};
const extractCampusUnit = (entry) => {
  const job = entry.organizations?.[0]?.jobs?.[0];
  if (!job) return null;
  const associations = [];
  const collegeSchool = job.collegeSchools?.[0];
  if (collegeSchool) {
    associations.push({
      title: collegeSchool.title,
      url: collegeSchool.link?.url
    });
  }
  const campusUnit = job.campusUnits?.[0];
  if (campusUnit) {
    associations.push({ title: campusUnit.title, url: campusUnit.link?.url });
  }
  return associations.length > 0 ? associations : null;
};
const extractImageData = (entry, fullName) => {
  const headshotUrl = entry.headshot?.[0]?.url;
  if (!headshotUrl) return null;
  return {
    url: headshotUrl,
    altText: fullName
  };
};
const extractContactData = (entry) => {
  return {
    email: entry.email || null,
    website: entry.website || null,
    linkedin: entry.linkedin || null,
    twitter: entry.twitter || null,
    bluesky: entry.bluesky || null,
    substack: entry.substack || null
  };
};
const extractDescription = (entry, displayType) => {
  return entry.summary?.html || null;
};
const extractPronouns = (entry) => {
  return entry.pronouns || null;
};
const extractTopics = (entry) => {
  const topics = entry.topics;
  if (!topics || topics.length === 0) {
    return null;
  }
  return topics.slice(0, 3).map((topic) => topic.title).join(", ");
};
const createNameElement = (fullName, url, containerTag) => {
  return createTextWithLink({
    text: fullName,
    url,
    containerTag
  });
};
const createJobElement = (jobTitle) => {
  if (!jobTitle) return null;
  return createTextContainer({ text: jobTitle });
};
const createAssociationElement = (associations) => {
  if (!associations || associations.length === 0) return null;
  if (associations.length === 1) {
    const association = associations[0];
    if (association.url) {
      return createTextWithLink({
        text: association.title,
        url: association.url
      });
    }
    return createTextContainer({ text: association.title });
  }
  const container = document.createElement("p");
  associations.forEach((association, index) => {
    if (association.url) {
      const link = document.createElement("a");
      link.href = association.url;
      link.textContent = association.title;
      container.appendChild(link);
    } else {
      container.appendChild(document.createTextNode(association.title));
    }
    if (index < associations.length - 1) {
      container.appendChild(document.createTextNode(", "));
    }
  });
  return container;
};
const createImageElement = (imageData, linkUrl, linkLabel) => {
  if (!imageData) return null;
  return createImageOrLinkedImage({
    imageUrl: imageData.url,
    altText: imageData.altText,
    linkUrl,
    linkLabel
  });
};
const createDescriptionElement = (description) => {
  if (!description) return null;
  return createTextContainer({ text: description, allowHTML: true });
};
const createPronounsElement = (pronouns) => {
  if (!pronouns) return null;
  return createTextContainer({ text: pronouns });
};
const createExpertiseElement = (expertise) => {
  if (!expertise) return null;
  return createTextContainer({ text: expertise });
};
const createContactElements = (contactData) => {
  return CONTACT_CONFIGS.reduce(
    (elements, config) => {
      const value = contactData[config.key];
      if (!value) return elements;
      const element = createTextWithLink({
        text: config.label(value),
        url: config.url(value)
      });
      elements[config.key] = element;
      return elements;
    },
    {}
  );
};
const createBlockCardProps = (entry, options) => {
  const { isThemeDark = false } = options;
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const campusUnit = extractCampusUnit(entry);
  const imageData = extractImageData(entry, fullName);
  const pronouns = extractPronouns(entry);
  const topics = extractTopics(entry);
  const name = createNameElement(fullName, profileUrl);
  createJobElement(jobTitle);
  const campusUnitElement = createAssociationElement(campusUnit);
  const image = createImageElement(
    imageData,
    profileUrl,
    `View profile for ${fullName}`
  );
  const pronounsElement = createPronounsElement(pronouns);
  const topicsElement = createExpertiseElement(topics);
  return person.block({
    name,
    slotOne: topicsElement,
    slotTwo: campusUnitElement,
    slotThreeItalic: pronounsElement,
    image,
    isThemeDark
  });
};
const createListCardProps = (entry, options) => {
  const { isThemeDark = false } = options;
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const campusUnit = extractCampusUnit(entry);
  const imageData = extractImageData(entry, fullName);
  const pronouns = extractPronouns(entry);
  const topics = extractTopics(entry);
  const name = createNameElement(fullName, profileUrl);
  createJobElement(jobTitle);
  const campusUnitElement = createAssociationElement(campusUnit);
  const image = createImageElement(
    imageData,
    profileUrl,
    `View profile for ${fullName}`
  );
  const pronounsElement = createPronounsElement(pronouns);
  const topicsElement = createExpertiseElement(topics);
  return person.list({
    name,
    slotOne: topicsElement,
    slotThreeItalic: pronounsElement,
    slotTwo: campusUnitElement,
    image,
    isThemeDark
  });
};
const createTabularCardProps = (entry, options) => {
  const { isThemeDark = false } = options;
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const campusUnit = extractCampusUnit(entry);
  const imageData = extractImageData(entry, fullName);
  const contactData = extractContactData(entry);
  const pronouns = extractPronouns(entry);
  const topics = extractTopics(entry);
  const name = createNameElement(fullName, profileUrl);
  createJobElement(jobTitle);
  const campusUnitElement = createAssociationElement(campusUnit);
  const image = createImageElement(
    imageData,
    profileUrl,
    `View profile for ${fullName}`
  );
  const contactElements = createContactElements(contactData);
  const pronounsElement = createPronounsElement(pronouns);
  const topicsElement = createExpertiseElement(topics);
  return person.tabular({
    name,
    slotOne: topicsElement,
    slotThreeItalic: pronounsElement,
    slotTwo: campusUnitElement,
    image,
    ...contactElements,
    isThemeDark
  });
};
const createOverlayCardProps = (entry, options) => {
  const { isThemeDark = false } = options;
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  extractPrimaryJobTitle(entry);
  const campusUnit = extractCampusUnit(entry);
  const topics = extractTopics(entry);
  const imageData = extractImageData(entry, fullName);
  const headline = createNameElement(fullName, profileUrl);
  const topicsElement = createExpertiseElement(topics);
  const campusUnitElement = createAssociationElement(campusUnit);
  const backgroundImage = createImageElement(
    imageData,
    profileUrl,
    `View profile for ${fullName}`
  );
  return card.overlay.image({
    eyebrow: campusUnitElement,
    headline,
    text: topicsElement,
    backgroundImage,
    isThemeDark
  });
};
const mapExpertToBioProps = (entry, displayType, isThemeDark = false) => {
  const fullName = buildFullName(entry);
  const profileUrl = buildProfileUrl(entry);
  const jobTitle = extractPrimaryJobTitle(entry);
  const campusUnit = extractCampusUnit(entry);
  const imageData = extractImageData(entry, fullName);
  const description = extractDescription(entry);
  const pronouns = extractPronouns(entry);
  const topics = extractTopics(entry);
  const name = createNameElement(fullName, profileUrl, "h1");
  createJobElement(jobTitle);
  const campusUnitElement = createAssociationElement(campusUnit);
  const image = createImageElement(imageData);
  const descriptionElement = createDescriptionElement(description);
  const pronounsElement = createPronounsElement(pronouns);
  const topicsElement = createExpertiseElement(topics);
  return {
    name,
    slotTwoItalic: pronounsElement,
    slotFour: campusUnitElement,
    slotOne: topicsElement,
    phone: null,
    address: null,
    additionalContact: null,
    image,
    description: descriptionElement,
    isThemeDark
  };
};
const expertsDisplayStrategy = {
  layoutType: "list",
  mapEntryToCard: (entry, options) => {
    const { isOverlay = false, cardType = "block" } = options;
    if (isOverlay && entry.headshot?.[0]?.url) {
      return createOverlayCardProps(entry, options);
    }
    switch (cardType) {
      case "list":
        return createListCardProps(entry, options);
      case "tabular":
        return createTabularCardProps(entry, options);
      default:
        return createBlockCardProps(entry, options);
    }
  }
};
export {
  buildFullName,
  expertsDisplayStrategy,
  extractTopics,
  mapExpertToBioProps
};
//# sourceMappingURL=experts.js.map
